import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  alpha,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const ReceivablesSummary = ({ summaryStats, loading }) => {
  const theme = useTheme();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const summaryCards = [
    {
      title: 'Total Outstanding',
      value: formatCurrency(summaryStats?.totalOutstanding || 0),
      helperText: `${summaryStats?.unpaidCount || 0} unpaid invoices`,
      icon: <AttachMoneyIcon sx={{ fontSize: 28 }} />,
      accent: 'error'
    },
    {
      title: 'Overdue',
      value: formatCurrency(summaryStats?.overdueAmount || 0),
      helperText: `${summaryStats?.overdueCount || 0} overdue invoices`,
      icon: <WarningIcon sx={{ fontSize: 28 }} />,
      accent: 'warning'
    },
    {
      title: 'Due Next 30 Days',
      value: formatCurrency(summaryStats?.dueNext30Days || 0),
      helperText: `${summaryStats?.dueNext30DaysCount || 0} invoices`,
      icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
      accent: 'info'
    },
    {
      title: 'Total Paid',
      value: formatCurrency(summaryStats?.totalPaid || 0),
      helperText: `${summaryStats?.paidCount || 0} paid invoices`,
      icon: <CheckCircleIcon sx={{ fontSize: 28 }} />,
      accent: 'success'
    }
  ];

  if (loading) {
    return (
      <Grid container spacing={3} sx={{ mb: 1}}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ 
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">Loading...</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mb: 1}}>
      {summaryCards.map((card, index) => {
        const paletteMap = {
          primary: theme.palette.primary,
          success: theme.palette.success,
          warning: theme.palette.warning,
          error: theme.palette.error,
          info: theme.palette.info
        };
        const accentPalette = paletteMap[card.accent] || theme.palette.primary;

        return (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(accentPalette.main, 0.08)}, ${alpha(accentPalette.main, 0.02)})`,
                border: card.accent === 'primary' 
                  ? `1px solid ${theme.palette.grey[400]}`
                  : `1px solid ${accentPalette.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(accentPalette.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(accentPalette.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 500,
                        color: card.accent === 'primary' ? 'primary.main' : `${card.accent}.main`,
                        mb: 1,
                        fontSize: card.accent === 'warning' || card.accent === 'success' ? '1.5rem' : '2rem',
                        lineHeight: 1
                      }}
                    >
                      {card.value}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.primary"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}
                    >
                      {card.title}
                    </Typography>
                    {card.helperText && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}
                      >
                        {card.helperText}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: card.accent === 'primary'
                        ? `linear-gradient(${alpha(accentPalette.main, 0.1)}, ${alpha(accentPalette.main, 0.05)})`
                        : accentPalette.main,
                      color: card.accent === 'primary' ? 'text.secondary' : 'white',
                      border: card.accent === 'primary'
                        ? `1px solid ${alpha(accentPalette.main, 0.2)}`
                        : `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      backdropFilter: card.accent === 'primary' ? 'blur(10px)' : 'none',
                      boxShadow: card.accent === 'primary'
                        ? `0 8px 24px ${alpha(accentPalette.main, 0.3)}`
                        : `0 6px 18px ${alpha(accentPalette.main, 0.3)}`
                    }}
                  >
                    {card.icon}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default ReceivablesSummary;